import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Box } from '@dixa/lib-shared-habla';
import { convertChannelCasing } from 'lib-shared-api-utils';
import partition from 'lodash/partition';
import React from 'react';
import { channelAttributes, mediaToChannels } from 'utils/channels';
import { getChannelFeatureFlagsStatus } from 'utils/featureFlagging/channelFeatureFlags';
const [phoneChannels, voiceMailChannels] = partition(mediaToChannels['speak'], (channel) => channel !== 'voicemail');
const emailChannels = mediaToChannels['email'];
const chatChannels = mediaToChannels['interactivechat'];
const messagingChannels = mediaToChannels['messaging'];
const getWorkloadByChannel = (presence, channel) => {
    const workload = presence.presence.presence_status.load_descriptions.filter((load) => {
        var _a;
        return ((_a = load.conversation) === null || _a === void 0 ? void 0 : _a.initial_channel) ===
            convertChannelCasing(channel, 'truncate');
    });
    return workload.length || 0;
};
export const getNumberOfConversations = (presence, channels) => {
    return channels
        .map((channel) => getWorkloadByChannel(presence, channel))
        .reduce((res, channelWorkload) => res + channelWorkload, 0);
};
const getChannelGroups = () => {
    const featureFlags = getChannelFeatureFlagsStatus();
    const filterByFeatureFlag = (channel) => {
        return featureFlags.get(channel);
    };
    return {
        phoneChannels: phoneChannels.filter(filterByFeatureFlag),
        voiceMailChannels: voiceMailChannels.filter(filterByFeatureFlag),
        emailChannels: emailChannels.filter(filterByFeatureFlag),
        chatChannels: chatChannels.filter(filterByFeatureFlag),
        messagingChannels: messagingChannels.filter(filterByFeatureFlag),
    };
};
export const getConversationGroups = (workloads, limits, fPresence) => {
    if (!fPresence)
        return [];
    const { phoneChannels, voiceMailChannels, emailChannels, chatChannels, messagingChannels, } = getChannelGroups();
    const phoneCount = getNumberOfConversations(fPresence, phoneChannels);
    const voicemailCount = getNumberOfConversations(fPresence, voiceMailChannels);
    const emailCount = getNumberOfConversations(fPresence, emailChannels);
    const chatCount = getNumberOfConversations(fPresence, chatChannels);
    const messagingCount = getNumberOfConversations(fPresence, messagingChannels);
    return [
        {
            id: 'phone',
            icon: 'phone-bold',
            conversationCount: phoneCount,
            active: isActiveOnChannelsGroup(fPresence, phoneChannels),
            channels: phoneChannels,
            limit: 1,
            tooltipContent: (_jsx(TooltipContent, Object.assign({ conversationCount: phoneCount, conversationLimit: 1, text: 'conversations', channelBreakdowns: getChannelBreakdowns(fPresence, phoneChannels) }, { children: _jsx(PhoneChannelBreakdown, { presence: fPresence, workloads: workloads, conversationCount: phoneCount }) }))),
        },
        {
            id: 'voicemail',
            icon: 'record-tape-bold',
            conversationCount: voicemailCount,
            active: isActiveOnChannelsGroup(fPresence, voiceMailChannels),
            channels: voiceMailChannels,
            limit: limits.voicemail,
            tooltipContent: (_jsx(TooltipContent, { conversationCount: voicemailCount, conversationLimit: limits['voicemail'], text: 'voicemails', channelBreakdowns: getChannelBreakdowns(fPresence, voiceMailChannels) })),
        },
        {
            id: 'email',
            icon: 'mail-bold',
            conversationCount: emailCount,
            active: isActiveOnChannelsGroup(fPresence, emailChannels),
            channels: emailChannels,
            limit: limits['email'],
            tooltipContent: (_jsx(TooltipContent, { conversationCount: emailCount, conversationLimit: limits['email'], text: 'conversations', channelBreakdowns: getChannelBreakdowns(fPresence, emailChannels) })),
        },
        {
            id: 'chat',
            icon: 'chat-lines-bold',
            conversationCount: chatCount,
            active: isActiveOnChannelsGroup(fPresence, chatChannels),
            channels: chatChannels,
            limit: limits['chat'],
            tooltipContent: (_jsx(TooltipContent, { conversationCount: chatCount, conversationLimit: limits['chat'], text: 'chats', channelBreakdowns: getChannelBreakdowns(fPresence, chatChannels) })),
        },
        {
            id: 'messaging',
            icon: 'messaging-bold',
            conversationCount: messagingCount,
            active: isActiveOnChannelsGroup(fPresence, messagingChannels),
            channels: messagingChannels,
            limit: limits['messaging'],
            tooltipContent: (_jsx(TooltipContent, { conversationCount: messagingCount, conversationLimit: limits['messaging'], text: 'conversations', channelBreakdowns: getChannelBreakdowns(fPresence, messagingChannels) })),
        },
    ];
};
export const TooltipContent = ({ conversationCount: count, conversationLimit: limit, text, channelBreakdowns = null, children, }) => {
    if (channelBreakdowns && channelBreakdowns.length) {
        return (_jsxs(_Fragment, { children: [`${count} / ${limit} ${text}`, _jsx(Box, { as: "hr", margin: "100 0" }), channelBreakdowns.map((channelBreakdown) => (_jsxs(_Fragment, { children: [channelBreakdown, _jsx("br", {})] }))), children] }));
    }
    return _jsx(_Fragment, { children: `${count} / ${limit} ${text}` });
};
const PhoneChannelBreakdown = ({ presence, workloads, conversationCount, }) => {
    var _a, _b;
    if (!conversationCount || !presence)
        return null;
    const loadDescriptions = presence.presence.presence_status.load_descriptions;
    const filteredLoadDescriptions = loadDescriptions.filter((ld) => ld.conversation);
    const externalPhoneConversations = (((_a = workloads['externalwork']) === null || _a === void 0 ? void 0 : _a.length) || 0) +
        (((_b = workloads['pstnphone']) === null || _b === void 0 ? void 0 : _b.length) || 0);
    const nonLivePhoneConversations = filteredLoadDescriptions.filter((o) => o.load_type === 'nonlive' && o.conversation_channel === 'pstnphone').length;
    const internalPhoneConversations = filteredLoadDescriptions.filter((o) => o.load_type === 'internalwork' && o.conversation_channel === 'pstnphone').length;
    return (_jsxs(_Fragment, { children: [_jsx(Box, { as: "hr", margin: "100 0" }), externalPhoneConversations, " external", _jsx("br", {}), " ", nonLivePhoneConversations, " abandoned", _jsx("br", {}), " ", internalPhoneConversations, " internal"] }));
};
const isActiveOnChannelsGroup = (presence, channels) => {
    const { presence: userPresence } = presence;
    const { working_channels: workingChannels } = userPresence;
    const active = workingChannels.some((channel) => channel.conversation_channel &&
        channels.includes(convertChannelCasing(channel.conversation_channel, 'snake')) &&
        channel.working);
    return active;
};
const getChannelBreakdowns = (presence, channels) => {
    if (channels.length < 2)
        return [];
    const specificWorkloads = channels.map((channel) => {
        const count = getWorkloadByChannel(presence, channel);
        return [channel, count];
    });
    const hasSomeWorkload = specificWorkloads.some(([, count]) => count > 0);
    if (!hasSomeWorkload)
        return [];
    return specificWorkloads.map(([channel, count]) => {
        return `${count} ${channelAttributes[channel].name}`;
    });
};
